<script setup lang="ts">
// A button component for proceeding to the next question or action in the quiz game.

import { onMounted, onUnmounted, ref } from 'vue';

const emits = defineEmits<{
  (e: 'click'): void;
}>();

const props = defineProps<{
  loading?: boolean;
}>();

const buttonRef = ref<any>(null);

const isFocusableElement = (el: Element | null): boolean => {
  if (!el || el === document.body) {
    return false;
  }
  const focusableTags = ['INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'A'];
  if (focusableTags.includes(el.tagName)) {
    return true;
  }
  return el.hasAttribute('tabindex') && el.getAttribute('tabindex') !== '-1';
};

const handleSpace = (e: KeyboardEvent) => {
  // If the user currently has a focus on an input element, we don't want to trigger the button click.
  if (e.code === 'Space' && !props.loading && !isFocusableElement(document.activeElement)) {
    e.preventDefault();
    // Programmatically click the button to trigger native navigation (for href)
    if (buttonRef.value?.$el) {
      buttonRef.value.$el.click();
    } else {
      emits('click');
    }
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleSpace);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleSpace);
});
</script>

<template>
  <v-btn
    ref="buttonRef"
    size="large"
    class="text-none next-button"
    @click="emits('click')"
    @keyup.space.prevent="emits('click')"
    :loading="loading"
  >
    <template #prepend>
      <slot name="prepend"></slot>
    </template>
    <template #append>
      <slot name="append"></slot>
    </template>
    <span class="button-text chalkduster-font"><slot></slot></span>
  </v-btn>
</template>

<style scoped lang="scss">
.next-button {
  font-size: 1.15rem;
  font-family: 'Chalkduster', sans-serif;
  background-image: url('../assets/Color_BG.svg');
  background-size: cover;
  background-position: center;
  background-color: transparent !important;
  overflow: hidden;

  &::before {
    content: '';
    background-color: #000000;
    opacity: 0.1;
    position: absolute;
    inset: 0;
  }

  .button-text {
    color: white !important;
    z-index: 1;
  }

  :deep(.v-btn__overlay),
  :deep(.v-btn__underlay) {
    display: none;
  }
}
</style>
